import React, {useEffect, useState} from "react";
import AdminSidebar from "../components/sidebar/admin_sidebar";
import PageTitle from "../../../components/page_title/page_title";
import axiosInstance from "../../../AxiosInstance";
import styles from "./subscriptions.module.css";
import sidebarStyles from "../components/sidebar/admin_sidebar.module.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faTimes, faEye, faSpinner, faFilter, faSearch, faFileInvoiceDollar, faUsers, faPlay, faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import {Modal as BootstrapModal} from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import {formatSuspensionReason} from "../../../utils/suspensionReason";

export default function AdminSubscriptions() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('subscriptions');
    const [subscriptions, setSubscriptions] = useState([]);
    const [pendingInvoices, setPendingInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending_payment');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [processing, setProcessing] = useState(null);
    const [actionSuccess, setActionSuccess] = useState('');
    const [actionError, setActionError] = useState('');

    const statusLabels = {
        'pending_payment': t('admin.subscriptions.status.pendingPayment'),
        'active': t('admin.subscriptions.status.active'),
        'cancelled': t('admin.subscriptions.status.cancelled'),
        'past_due': t('admin.subscriptions.status.pastDue'),
        'suspended': t('admin.subscriptions.status.suspended'),
        'trialing': t('admin.subscriptions.status.trialing')
    };

    const statusColors = {
        'pending_payment': styles.statusPending,
        'active': styles.statusActive,
        'cancelled': styles.statusCancelled,
        'past_due': styles.statusPastDue,
        'suspended': styles.statusSuspended,
        'trialing': styles.statusTrialing
    };

    useEffect(() => {
        if (activeTab === 'subscriptions') {
            fetchSubscriptions();
        } else {
            fetchPendingInvoices();
        }
    }, [filter, activeTab]);

    // Load pending invoices count on mount for badge
    useEffect(() => {
        const loadPendingCount = async () => {
            try {
                const response = await axiosInstance.get('subscription_admin/pending_invoices/');
                setPendingInvoices(response.data || []);
            } catch (error) {
                console.error('Error loading pending invoices count:', error);
            }
        };
        loadPendingCount();
    }, []);

    const fetchPendingInvoices = async () => {
        setLoading(true);
        setActionError('');
        try {
            const response = await axiosInstance.get('subscription_admin/pending_invoices/');
            setPendingInvoices(response.data || []);
        } catch (error) {
            console.error('Error fetching pending invoices:', error);
            setActionError(error.response?.data?.error || 'Error loading pending invoices');
            setPendingInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkPaid = async (invoiceId) => {
        setProcessing(invoiceId);
        setActionError('');
        setActionSuccess('');
        try {
            await axiosInstance.post(`subscription_admin/invoices/${invoiceId}/mark_paid/`);
            setActionSuccess(t('admin.subscriptions.invoiceMarkedPaid'));
            fetchPendingInvoices();
        } catch (error) {
            setActionError(error.response?.data?.error || 'Error marking invoice as paid');
        } finally {
            setProcessing(null);
        }
    };

    const fetchSubscriptions = async () => {
        setLoading(true);
        setActionError('');
        try {
            const params = filter !== 'all' ? {status: filter} : {};
            const response = await axiosInstance.get('subscription_admin/list/', {params});
            const data = response.data;
            // Handle both paginated and non-paginated responses
            if (Array.isArray(data)) {
                setSubscriptions(data);
            } else if (data && Array.isArray(data.results)) {
                setSubscriptions(data.results);
            } else {
                setSubscriptions([]);
            }
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            if (error.response?.status === 404) {
                setActionError('The subscription admin endpoint is not available. Make sure the backend has implemented the endpoint: GET /api/subscription_admin/list/');
            } else {
                setActionError(error.response?.data?.message || 'Error loading subscriptions');
            }
            setSubscriptions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (subscriptionId) => {
        setProcessing(subscriptionId);
        setActionError('');
        setActionSuccess('');
        try {
            await axiosInstance.post(`subscription_admin/${subscriptionId}/approve/`);
            setActionSuccess(t('admin.subscriptions.approved'));
            fetchSubscriptions();
            setShowDetailModal(false);
        } catch (error) {
            setActionError(error.response?.data?.message || 'Error approving subscription');
        } finally {
            setProcessing(null);
        }
    };

    const handleReactivate = async (subscriptionId) => {
        setProcessing(subscriptionId);
        setActionError('');
        setActionSuccess('');
        try {
            await axiosInstance.post(`subscription_admin/${subscriptionId}/activate/`);
            setActionSuccess(t('admin.subscriptions.reactivated'));
            fetchSubscriptions();
            setShowDetailModal(false);
        } catch (error) {
            setActionError(error.response?.data?.message || t('admin.subscriptions.errorReactivating'));
        } finally {
            setProcessing(null);
        }
    };

    const handleReject = async (subscriptionId) => {
        setProcessing(subscriptionId);
        setActionError('');
        setActionSuccess('');
        try {
            await axiosInstance.post(`subscription_admin/${subscriptionId}/reject/`);
            setActionSuccess(t('admin.subscriptions.rejected'));
            fetchSubscriptions();
            setShowDetailModal(false);
        } catch (error) {
            setActionError(error.response?.data?.message || 'Error rejecting subscription');
        } finally {
            setProcessing(null);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-PE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const isExpiringSoon = (dateString) => {
        if (!dateString) return false;
        const expiresAt = new Date(dateString);
        const now = new Date();
        const daysUntilExpiry = (expiresAt - now) / (1000 * 60 * 60 * 24);
        return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
    };

    const filteredSubscriptions = subscriptions.filter(sub => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            sub.corporate_group?.name?.toLowerCase().includes(search) ||
            sub.corporate_group?.contact_email?.toLowerCase().includes(search) ||
            sub.payment_plan?.name?.toLowerCase().includes(search)
        );
    });

    const openDetail = (subscription) => {
        setSelectedSubscription(subscription);
        setShowDetailModal(true);
        setActionError('');
        setActionSuccess('');
    };

    return (
        <div className={sidebarStyles.adminContent}>
            <AdminSidebar active={3}/>

            <div className={'mx-5'}>
                <PageTitle title={'Subscriptions'}/>

                {actionSuccess && (
                    <div className={styles.successAlert}>
                        {actionSuccess}
                    </div>
                )}

                {actionError && (
                    <div className={styles.errorAlert}>
                        {actionError}
                    </div>
                )}

                {/* Tabs */}
                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'subscriptions' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('subscriptions')}
                    >
                        <FontAwesomeIcon icon={faUsers} style={{marginRight: '8px'}}/>
                        {t('admin.subscriptions.tabs.subscriptions')}
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'invoices' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('invoices')}
                    >
                        <FontAwesomeIcon icon={faFileInvoiceDollar} style={{marginRight: '8px'}}/>
                        {t('admin.subscriptions.tabs.pendingInvoices')}
                        {pendingInvoices.length > 0 && (
                            <span className={styles.tabBadge}>{pendingInvoices.length}</span>
                        )}
                    </button>
                </div>

                {activeTab === 'subscriptions' && (
                <div className={styles.toolbar}>
                    <div className={styles.filters}>
                        <FontAwesomeIcon icon={faFilter} className={styles.filterIcon}/>
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className={styles.filterSelect}
                        >
                            <option value="all">{t('admin.subscriptions.filter.all')}</option>
                            <option value="pending_payment">{t('admin.subscriptions.filter.pendingPayment')}</option>
                            <option value="active">{t('admin.subscriptions.filter.active')}</option>
                            <option value="cancelled">{t('admin.subscriptions.filter.cancelled')}</option>
                            <option value="past_due">{t('admin.subscriptions.filter.pastDue')}</option>
                            <option value="suspended">{t('admin.subscriptions.filter.suspended')}</option>
                            <option value="trialing">{t('admin.subscriptions.filter.trialing')}</option>
                        </select>
                    </div>

                    <div className={styles.searchBox}>
                        <FontAwesomeIcon icon={faSearch} className={styles.searchIcon}/>
                        <input
                            type="text"
                            placeholder={t('admin.subscriptions.search')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.searchInput}
                        />
                    </div>
                </div>
                )}

                {/* Subscriptions Table */}
                {activeTab === 'subscriptions' && (
                <div className={styles.tableContainer}>
                    {loading ? (
                        <div className={styles.loadingState}>
                            <FontAwesomeIcon icon={faSpinner} spin size="2x"/>
                            <p>{t('admin.subscriptions.loading')}</p>
                        </div>
                    ) : filteredSubscriptions.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>{t('admin.subscriptions.noFound')}</p>
                        </div>
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t('admin.subscriptions.columns.corporateGroup')}</th>
                                    <th>{t('admin.subscriptions.columns.plan')}</th>
                                    <th>{t('admin.subscriptions.columns.cycle')}</th>
                                    <th>{t('admin.subscriptions.columns.amount')}</th>
                                    <th>{t('admin.subscriptions.columns.limits')}</th>
                                    <th>{t('admin.columns.status')}</th>
                                    <th>{t('admin.subscriptions.columns.requestDate')}</th>
                                    <th>{t('admin.columns.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSubscriptions.map((sub) => (
                                    <tr key={sub.id}>
                                        <td>
                                            <div className={styles.userCell}>
                                                <span className={styles.userName}>
                                                    {sub.corporate_group?.name || '-'}
                                                </span>
                                                <span className={styles.userEmail}>{sub.corporate_group?.contact_email}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={styles.planBadge}>{sub.payment_plan?.name}</span>
                                        </td>
                                        <td>{sub.billing_cycle === 'yearly' ? t('admin.subscriptions.cycle.yearly') : t('admin.subscriptions.cycle.monthly')}</td>
                                        <td className={styles.amount}>
                                            ${sub.payment_plan?.price?.toFixed(2) || '-'}
                                        </td>
                                        <td>
                                            <span className={styles.limitsCell}>
                                                {sub.max_devices} disp. / {sub.max_users} users / {sub.max_companies} emp.
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`${styles.statusBadge} ${statusColors[sub.status]}`}>
                                                {statusLabels[sub.status] || sub.status}
                                            </span>
                                        </td>
                                        <td>{formatDate(sub.created_at)}</td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button
                                                    className={styles.viewButton}
                                                    onClick={() => openDetail(sub)}
                                                    title="View details"
                                                >
                                                    <FontAwesomeIcon icon={faEye}/>
                                                </button>
                                                {sub.status === 'pending_payment' && (
                                                    <>
                                                        <button
                                                            className={styles.approveButton}
                                                            onClick={() => handleApprove(sub.id)}
                                                            disabled={processing === sub.id}
                                                            title="Approve"
                                                        >
                                                            {processing === sub.id ? (
                                                                <FontAwesomeIcon icon={faSpinner} spin/>
                                                            ) : (
                                                                <FontAwesomeIcon icon={faCheck}/>
                                                            )}
                                                        </button>
                                                        <button
                                                            className={styles.rejectButton}
                                                            onClick={() => handleReject(sub.id)}
                                                            disabled={processing === sub.id}
                                                            title="Reject"
                                                        >
                                                            <FontAwesomeIcon icon={faTimes}/>
                                                        </button>
                                                    </>
                                                )}
                                                {sub.status === 'suspended' && (
                                                    <button
                                                        className={styles.approveButton}
                                                        onClick={() => handleReactivate(sub.id)}
                                                        disabled={processing === sub.id}
                                                        title={t('admin.subscriptions.reactivate')}
                                                    >
                                                        {processing === sub.id ? (
                                                            <FontAwesomeIcon icon={faSpinner} spin/>
                                                        ) : (
                                                            <FontAwesomeIcon icon={faPlay}/>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                )}

                {activeTab === 'subscriptions' && (
                    <div className={styles.policyNote}>
                        <FontAwesomeIcon icon={faInfoCircle} className={styles.policyIcon}/>
                        <div>
                            <strong>{t('admin.subscriptions.policyTitle')}</strong>
                            <p>{t('admin.subscriptions.policyBody')}</p>
                        </div>
                    </div>
                )}

                {/* Pending Invoices Table */}
                {activeTab === 'invoices' && (
                <div className={styles.tableContainer}>
                    {loading ? (
                        <div className={styles.loadingState}>
                            <FontAwesomeIcon icon={faSpinner} spin size="2x"/>
                            <p>{t('admin.subscriptions.loadingInvoices')}</p>
                        </div>
                    ) : pendingInvoices.length === 0 ? (
                        <div className={styles.emptyState}>
                            <FontAwesomeIcon icon={faCheck} size="2x" style={{color: '#4caf50'}}/>
                            <p>{t('admin.subscriptions.noPendingInvoices')}</p>
                        </div>
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t('admin.subscriptions.columns.invoice')}</th>
                                    <th>{t('admin.subscriptions.columns.client')}</th>
                                    <th>{t('admin.subscriptions.columns.period')}</th>
                                    <th>{t('admin.subscriptions.columns.amount')}</th>
                                    <th>{t('admin.columns.status')}</th>
                                    <th>{t('admin.subscriptions.columns.dueDate')}</th>
                                    <th>{t('admin.columns.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pendingInvoices.map((inv) => (
                                    <tr key={inv.id}>
                                        <td>
                                            <span className={styles.userName}>{inv.invoice_number}</span>
                                        </td>
                                        <td>
                                            <div className={styles.userCell}>
                                                <span className={styles.userName}>
                                                    {inv.corporate_group?.name || inv.company?.name || '-'}
                                                </span>
                                                <span className={styles.userEmail}>
                                                    {inv.corporate_group?.contact_email || '-'}
                                                </span>
                                            </div>
                                        </td>
                                        <td>{inv.period}</td>
                                        <td className={styles.invoiceAmount}>
                                            ${inv.amount?.toFixed(2) || '-'}
                                        </td>
                                        <td>
                                            <span className={`${styles.invoiceStatus} ${isExpiringSoon(inv.expires_at) ? styles.invoiceExpiresSoon : styles.invoicePending}`}>
                                                {isExpiringSoon(inv.expires_at) ? t('admin.subscriptions.expiringSoon') : t('admin.subscriptions.pending')}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`${styles.expiresAt} ${isExpiringSoon(inv.expires_at) ? styles.expiresSoon : ''}`}>
                                                {formatDate(inv.expires_at)}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                className={styles.confirmPaymentButton}
                                                onClick={() => handleMarkPaid(inv.id)}
                                                disabled={processing === inv.id}
                                            >
                                                {processing === inv.id ? (
                                                    <FontAwesomeIcon icon={faSpinner} spin/>
                                                ) : (
                                                    <>
                                                        <FontAwesomeIcon icon={faCheck}/>
                                                        {t('admin.subscriptions.confirmPayment')}
                                                    </>
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                )}
            </div>

            {/* Detail Modal */}
            <BootstrapModal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg">
                <div className={styles.modal}>
                    <div className={styles.modalHeader}>
                        <h2>{t('admin.subscriptions.details.title')}</h2>
                        <button className={styles.closeButton} onClick={() => setShowDetailModal(false)}>&times;</button>
                    </div>

                    {selectedSubscription && (
                        <div className={styles.modalBody}>
                            <div className={styles.detailSection}>
                                <h3>{t('admin.subscriptions.details.corporateGroup')}</h3>
                                <div className={styles.detailGrid}>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.name')}</span>
                                        <span className={styles.detailValue}>
                                            {selectedSubscription.corporate_group?.name || '-'}
                                        </span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.contactEmail')}</span>
                                        <span className={styles.detailValue}>{selectedSubscription.corporate_group?.contact_email || '-'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.detailSection}>
                                <h3>{t('admin.subscriptions.details.planInfo')}</h3>
                                <div className={styles.detailGrid}>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.plan')}</span>
                                        <span className={styles.detailValue}>{selectedSubscription.payment_plan?.name}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.billingCycle')}</span>
                                        <span className={styles.detailValue}>
                                            {selectedSubscription.billing_cycle === 'yearly' ? t('admin.subscriptions.cycle.yearly') : t('admin.subscriptions.cycle.monthly')}
                                        </span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.planPrice')}</span>
                                        <span className={styles.detailValue}>
                                            ${selectedSubscription.payment_plan?.price?.toFixed(2) || '-'} USD
                                        </span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.columns.status')}</span>
                                        <span className={`${styles.statusBadge} ${statusColors[selectedSubscription.status]}`}>
                                            {statusLabels[selectedSubscription.status]}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.detailSection}>
                                <h3>{t('admin.subscriptions.details.limits')}</h3>
                                <div className={styles.detailGrid}>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.maxDevices')}</span>
                                        <span className={styles.detailValue}>{selectedSubscription.max_devices || '-'}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.maxUsers')}</span>
                                        <span className={styles.detailValue}>{selectedSubscription.max_users || '-'}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.maxCompanies')}</span>
                                        <span className={styles.detailValue}>{selectedSubscription.max_companies || '-'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.detailSection}>
                                <h3>{t('admin.subscriptions.details.dates')}</h3>
                                <div className={styles.detailGrid}>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.requestDate')}</span>
                                        <span className={styles.detailValue}>{formatDate(selectedSubscription.created_at)}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.startDate')}</span>
                                        <span className={styles.detailValue}>{formatDate(selectedSubscription.start_date)}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.periodStart')}</span>
                                        <span className={styles.detailValue}>{formatDate(selectedSubscription.current_period_start)}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{t('admin.subscriptions.details.periodEnd')}</span>
                                        <span className={styles.detailValue}>{formatDate(selectedSubscription.current_period_end)}</span>
                                    </div>
                                    {selectedSubscription.suspended_at && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>{t('admin.subscriptions.details.suspendedOn')}</span>
                                            <span className={styles.detailValue}>{formatDate(selectedSubscription.suspended_at)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {selectedSubscription.suspension_reason && (
                                <div className={styles.detailSection}>
                                    <h3>{t('admin.subscriptions.details.suspensionReason')}</h3>
                                    <p className={styles.suspensionReason}>{formatSuspensionReason(selectedSubscription.suspension_reason, t, 'admin.subscriptions')}</p>
                                </div>
                            )}

                            {selectedSubscription.status === 'pending_payment' && (
                                <div className={styles.modalActions}>
                                    <button
                                        className={styles.rejectButtonLarge}
                                        onClick={() => handleReject(selectedSubscription.id)}
                                        disabled={processing === selectedSubscription.id}
                                    >
                                        {processing === selectedSubscription.id ? (
                                            <FontAwesomeIcon icon={faSpinner} spin/>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faTimes}/> {t('common.reject')}
                                            </>
                                        )}
                                    </button>
                                    <button
                                        className={styles.approveButtonLarge}
                                        onClick={() => handleApprove(selectedSubscription.id)}
                                        disabled={processing === selectedSubscription.id}
                                    >
                                        {processing === selectedSubscription.id ? (
                                            <FontAwesomeIcon icon={faSpinner} spin/>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faCheck}/> {t('admin.subscriptions.approvePayment')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}

                            {selectedSubscription.status === 'suspended' && (
                                <div className={styles.modalActions}>
                                    <button
                                        className={styles.approveButtonLarge}
                                        onClick={() => handleReactivate(selectedSubscription.id)}
                                        disabled={processing === selectedSubscription.id}
                                    >
                                        {processing === selectedSubscription.id ? (
                                            <FontAwesomeIcon icon={faSpinner} spin/>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faPlay}/> {t('admin.subscriptions.reactivate')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </BootstrapModal>
        </div>
    );
}
